/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useItemFetcher } from '@/hooks/use-item-fetcher';
import { useBasketUpdater } from '@/providers/basket';
import { useToast } from '@/components/toast';
import { resourceRoutes } from '@/route-paths';
import type { BasketActionResponse } from '@/routes/types/action-responses';
import { cn } from '@/lib/utils';

interface PdpCartStepperProps {
    /** Cart line item id being adjusted */
    itemId: string;
    /** Current quantity of this line in the basket (source of truth) */
    quantity: number;
    /**
     * Maximum quantity the shopper may reach (effective stock level or a
     * per-product cap). The increment control is disabled once reached.
     * Undefined means no client-side cap — the server remains authoritative.
     */
    max?: number;
    /** Product name for accessible control labels */
    productName?: string;
    /** Additional class names for the container */
    className?: string;
}

/**
 * PDP "in cart" stepper.
 *
 * Replaces the Add to Cart button on the product detail page once the current
 * product/variant is in the basket. Renders a single pill — `− N in Cart +` —
 * that adjusts the line quantity in place via the cart-item-update action.
 *
 * Quantity is owned by the basket; this component reflects the `quantity` prop
 * and shows an optimistic value only while an update is in flight, rolling back
 * on failure. Per product decision, the decrement control is disabled at 1
 * (removal happens from the cart, not here).
 */
export default function PdpCartStepper({
    itemId,
    quantity,
    max,
    productName,
    className,
}: PdpCartStepperProps): ReactElement {
    const { t } = useTranslation('product');
    const { t: tQuantity } = useTranslation('quantitySelector');
    const { t: tCommon } = useTranslation('common');
    const updateBasket = useBasketUpdater();
    const { addToast } = useToast();

    const fetcher = useItemFetcher({ itemId, componentName: 'pdp-cart-stepper' });
    const isLoading = fetcher.state === 'submitting';

    // Optimistic quantity shown while an update is in flight. `null` means "trust
    // the basket-derived prop". Cleared once the response is reconciled so the
    // prop (source of truth) drives the display again.
    const [pendingQuantity, setPendingQuantity] = useState<number | null>(null);
    const displayQuantity = pendingQuantity ?? quantity;

    const name = productName || tCommon('product');
    const decrementDisabled = isLoading || displayQuantity <= 1;
    const incrementDisabled = isLoading || (max != null && displayQuantity >= max);

    const submitQuantity = (nextQuantity: number) => {
        if (isLoading || nextQuantity < 1) return;
        setPendingQuantity(nextQuantity);
        const formData = new FormData();
        formData.append('itemId', itemId);
        formData.append('quantity', nextQuantity.toString());
        void fetcher.submit(formData, {
            method: 'PATCH',
            action: resourceRoutes.cartItemUpdate,
        });
    };

    // Reconcile the in-flight update once the action resolves: publish the new
    // basket revision on success, or roll back the optimistic value and surface
    // an error toast on failure.
    useEffect(() => {
        if (fetcher.state !== 'idle' || !fetcher.data) return;
        const data = fetcher.data as BasketActionResponse;
        if (data.success) {
            if (data.basket) {
                updateBasket(data.basket);
            }
        } else {
            addToast(t('failedToUpdateCart', { error: data.error?.message }), 'error');
        }
        setPendingQuantity(null);
        // addToast/updateBasket are stable; excluding them avoids needless re-runs.
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher.state, fetcher.data]);

    return (
        <div
            role="group"
            aria-label={tQuantity('quantityForProduct', { productName: name })}
            className={cn(
                'flex h-10 w-full items-center justify-between rounded-ui bg-primary text-primary-foreground select-none',
                className
            )}>
            <button
                type="button"
                onClick={() => submitQuantity(displayQuantity - 1)}
                disabled={decrementDisabled}
                aria-label={tQuantity('decreaseQuantityForProduct', { productName: name })}
                data-testid="pdp-stepper-decrement"
                className="flex h-full items-center px-6 text-xl font-semibold leading-none transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-[-2px] disabled:opacity-40 disabled:cursor-not-allowed">
                −
            </button>
            <span
                data-testid="pdp-stepper-count"
                aria-live="polite"
                className="flex-1 text-center text-base font-semibold leading-6">
                {t('inCart', { quantity: displayQuantity })}
            </span>
            <button
                type="button"
                onClick={() => submitQuantity(displayQuantity + 1)}
                disabled={incrementDisabled}
                aria-label={tQuantity('increaseQuantityForProduct', { productName: name })}
                data-testid="pdp-stepper-increment"
                className="flex h-full items-center px-6 text-xl font-semibold leading-none transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-[-2px] disabled:opacity-40 disabled:cursor-not-allowed">
                +
            </button>
        </div>
    );
}
